/**
 * NoticeBell — the bell in Studio's top bar and the notification list behind
 * it (Design's sketch "Studio - bell and notification list", Nino OK 12 Oct).
 *
 * A 44 px round outline button left of EN · ID with a blue unread count ("9+"
 * above nine), filled dark while open. Computer: a 420 px panel from the bell,
 * up to 660 px tall, scrolling inside. Phone: a sheet from under the top bar
 * with a grab handle and ✕. An unread "learner waiting" is pinned on top with
 * Live; then Today, Earlier this week, Earlier. Seat changes fold per session.
 * Tapping a row opens its Studio page and marks it read.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useI18n, type TFunction } from '@/lib/i18n';
import { formatDate, formatTime } from '@/lib/formatTime';
import { agoOf, bellCount, noticeTarget, sectionsFor, type Notice, type NoticeCard } from '@/lib/notices';
import type { FacilitatorPage } from '@/types/facilitator-portal';
import type { NoticeList } from '../hooks/useNoticeList';
import './NoticeBell.css';

const BELL = (size: number) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9a6 6 0 1 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </svg>
);

/* The family's still picture (the pop-up animation's last frame), from Design's sketch. */
type Icon = 'live' | 'tick' | 'clock' | 'head' | 'inv' | 'star';
const ICON_BY_TYPE: Record<string, Icon> = {
  learner_waiting: 'live',
  fac_session_confirmed: 'tick',
  fac_reminder_24h: 'clock',
  fac_reminder_15min: 'clock',
  weekly_schedule: 'clock',
  session_to_claim: 'inv',
  scores_to_review: 'star',
};
const ICON_BY_KIND: Record<string, Icon> = { celebrate: 'star', get_ready: 'tick', heads_up: 'head', invite: 'inv' };

function IconSvg({ icon }: { icon: Icon }) {
  const c = '#FAF0E6';
  return (
    <svg viewBox="0 0 56 56" aria-hidden>
      {icon === 'live' && <><circle cx="28" cy="28" r="11" fill="#FF0080" /><path d="M25 23.5v9l7.5-4.5z" fill="#fff" /></>}
      {icon === 'tick' && <><circle cx="28" cy="28" r="18" fill="none" stroke={c} strokeWidth="3" /><path d="M20 29l6 6 11-13" fill="none" stroke={c} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" /></>}
      {icon === 'clock' && <><circle cx="28" cy="28" r="18" fill="none" stroke={c} strokeWidth="3" /><path d="M28 28V18M28 28l8 4" stroke="rgba(250,240,230,.6)" strokeWidth="3.2" strokeLinecap="round" /></>}
      {icon === 'head' && <><circle cx="28" cy="28" r="18" fill="none" stroke={c} strokeWidth="3" /><circle cx="28" cy="20" r="2.4" fill={c} /><path d="M28 26v12" stroke={c} strokeWidth="3.4" strokeLinecap="round" /></>}
      {icon === 'inv' && <path d="M10 34c8-18 22-22 36-24M10 38c9-14 23-18 36-20M10 42c10-10 24-13 36-15" fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" />}
      {icon === 'star' && <path d="M28 12l4.6 10.4 11.4 1.2-8.6 7.6 2.5 11.2L28 36.6 18.1 42.4l2.5-11.2-8.6-7.6 11.4-1.2z" fill="none" stroke={c} strokeWidth="2.8" strokeLinejoin="round" />}
    </svg>
  );
}

const iconOf = (n: Notice): Icon => (n.type.startsWith('seat_') ? 'head' : ICON_BY_TYPE[n.type] ?? ICON_BY_KIND[n.kind] ?? 'head');

function agoText(createdAt: string, now: Date, t: TFunction, locale: string): string {
  const a = agoOf(createdAt, now);
  if (a.kind === 'now') return t('ntc.now');
  if (a.kind === 'min') return t('ntc.min', { n: a.n });
  // Just the time, as drawn ("10:12"): the zone name only crowds the row.
  if (a.kind === 'time') return formatTime(a.at).split(' · ')[0].replace(/\s\S+$/, '');
  if (a.kind === 'weekday') return a.at.toLocaleDateString(locale === 'id' ? 'id-ID' : 'en-US', { weekday: 'short' });
  return formatDate(a.at, { day: 'numeric', month: 'short' });
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

interface Props {
  list: NoticeList;
  onNavigate: (page: FacilitatorPage) => void;
}

export default function NoticeBell({ list, onNavigate }: Props) {
  const { t, locale } = useI18n();
  const wrap = useRef<HTMLDivElement>(null);
  const { open, setOpen } = list;

  // Closes on Escape and on a click outside it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open, setOpen]);

  const count = bellCount(list.unread);
  const now = new Date();
  const s = sectionsFor(list.items, now);
  const empty = list.loaded && !list.failed && list.items.length === 0;

  const go = (card: NoticeCard) => {
    list.markRead(card.notices.map((n) => n.id));
    const to = noticeTarget(card.notices[0].button);
    if (to) { onNavigate(to.page); setOpen(false); }
  };

  const row = (card: NoticeCard, pinned = false): ReactNode => {
    const n = card.notices[0];
    const folded = card.notices.length > 1;
    const target = noticeTarget(n.button);
    const weekly = n.type === 'weekly_schedule' && num(n.data?.sessions) !== null;
    const minutes = num(n.data?.minutes);
    const btnTone = pinned ? 'bl' : n.type === 'session_to_claim' ? 'p' : '';
    return (
      <div
        key={card.key}
        role="button"
        tabIndex={0}
        onClick={() => go(card)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(card); } }}
        className={'ntc-n' + (card.unread ? ' unread' : '') + (pinned ? ' urgent' : '') + (folded ? ' fold' : '')}
      >
        <span className={'ntc-u' + (card.unread ? '' : ' r')} aria-label={card.unread ? t('ntc.unread') : undefined} />
        <span className="ntc-ic"><IconSvg icon={iconOf(n)} /></span>
        <div className="ntc-tx">
          {pinned && <span className="ntc-live"><i />{t('ntc.live')}</span>}
          <div className="ntc-tt">
            <b>{folded ? t('ntc.fold_t', { count: card.notices.length }) : n.title}</b>
            <small>{agoText(n.createdAt, now, t, locale)}</small>
          </div>
          <div className="ntc-m">{n.message}</div>
          {weekly && (
            <div className="ntc-wk">
              <div><b>{num(n.data.sessions)}</b><small>{t('ntc.wk_sessions')}</small></div>
              {num(n.data.learners) !== null && <div><b>{num(n.data.learners)}</b><small>{t('ntc.wk_learners')}</small></div>}
              {minutes !== null && <div><b>{(Math.round(minutes / 6) / 10).toLocaleString(locale === 'id' ? 'id-ID' : 'en-US')}</b><small>{t('ntc.wk_hours')}</small></div>}
            </div>
          )}
          {folded && (
            <ul className="ntc-sub">
              {card.notices.map((x) => <li key={x.id}>{x.title} <small>· {agoText(x.createdAt, now, t, locale)}</small></li>)}
            </ul>
          )}
          {target && n.button?.label && (
            <div className="ntc-act"><span className={btnTone}>{n.button.label}</span></div>
          )}
        </div>
      </div>
    );
  };

  const group = (label: string, cards: NoticeCard[]) => cards.length > 0 && (
    <>
      <div className="ntc-gl">{label}</div>
      {cards.map((c) => row(c))}
    </>
  );

  return (
    <div className="ntc-wrap" ref={wrap}>
      <button
        type="button"
        className={'ntc-bell' + (open ? ' open' : '')}
        aria-expanded={open}
        aria-controls="fp-notices"
        aria-label={count ? t('ntc.bell_n', { n: list.unread }) : t('ntc.bell')}
        onClick={() => setOpen(!open)}
      >
        {BELL(20)}
        {count && !open && <b>{count}</b>}
      </button>
      {open && (
        <div className="ntc-panel" id="fp-notices" role="dialog" aria-label={t('ntc.title')}>
          <div className="ntc-grab" aria-hidden />
          <div className="ntc-h">
            <b>{t('ntc.title')}</b>
            <span className="ntc-h-r">
              {list.unread > 0 && <button type="button" onClick={list.markAllRead}>{t('ntc.mark_all')}</button>}
              <button type="button" className="ntc-x" aria-label={t('ntc.close')} onClick={() => setOpen(false)}>✕</button>
            </span>
          </div>
          {empty ? (
            <div className="ntc-empty">{BELL(34)}<b>{t('ntc.empty_t')}</b>{t('ntc.empty_s')}</div>
          ) : (
            <div className="ntc-list">
              {list.failed && <div className="ntc-fail">{t('ntc.failed')}</div>}
              {s.pinned.map((c) => row(c, true))}
              {group(t('ntc.today'), s.today)}
              {group(t('ntc.this_week'), s.week)}
              {group(t('ntc.earlier'), s.earlier)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
